// Home's `sync_health` widget for the Admin (CR-040, B11's share; G §9 "Failed sync is visible and retryable."). The
// server sends it to the Admin alone (`app/integrations/read.py sync_health`; DC-228 folds the HR Systems Admin into
// the Admin): per port how
// it is wired and how many syncs wait on someone, each row linking to System Admin › Integrations, where a failure is
// retried or recovered. An element builder like Home's own views: no hook, null when there is nothing to draw.

import type { ReactElement } from "react";
import type { WidgetOut } from "@/api/tools";
import { CORE_LABELS, Paragraph, Pill, Row, RowName, RULE_LABELS, Stack, Widget } from "@/mc";
import { SYSTEM_LABELS } from "@/views/b10/system/labels";

/** One port as the widget receives it. */
export interface SyncHealthPort {
  port: string;
  adapter: string;
  open_failures: number;
  last_success_at: string | null;
}

export interface SyncHealthData {
  ports: SyncHealthPort[];
  open_failures: number;
  oldest_open_failure_age_s: number | null;
  route: string;
}

/** The widget's data read defensively: an unknown shape draws nothing rather than a wrong count. */
export function syncHealthOf(data: Record<string, unknown>): SyncHealthData | null {
  const ports = Array.isArray(data["ports"]) ? (data["ports"] as SyncHealthPort[]) : null;
  if (ports === null) return null;
  return {
    ports: ports.filter((p) => typeof p?.port === "string"),
    open_failures: typeof data["open_failures"] === "number" ? data["open_failures"] : 0,
    oldest_open_failure_age_s: typeof data["oldest_open_failure_age_s"] === "number" ? data["oldest_open_failure_age_s"] : null,
    route: typeof data["route"] === "string" ? data["route"] : "",
  };
}

/** Widget `sync_health`. */
export function syncHealthWidget(widget: WidgetOut): ReactElement | null {
  const health = syncHealthOf(widget.data);
  if (health === null || health.ports.length === 0) return null;
  const href = health.route.startsWith("#") ? health.route : undefined;
  return (
    <Widget
      id={widget.key}
      kind="sections"
      roles={["admin"]}
      title={SYSTEM_LABELS.integrations}
      sub={CORE_LABELS.syncFailure}
      count={health.open_failures}
      level={2}
    >
      <Stack gap={4}>
        {health.ports.map((p) => (
          <Row key={p.port} cols={2} lead="70%" href={href}>
            <RowName name={<span className="mc-mono">{p.port}</span>} sub={<span className="mc-mono">{p.adapter}</span>} />
            {p.open_failures > 0 ? <Pill tone="red">{p.open_failures}</Pill> : <span className="mc-mono">0</span>}
          </Row>
        ))}
        <Paragraph sm>{RULE_LABELS.failedSyncRetryable}</Paragraph>
      </Stack>
    </Widget>
  );
}
